/**
 * Module dependencies.
 */

import { apiEndpoints } from 'app/core/config/api-endpoints';
import { appRoutes } from 'app/routes';
import { identity, pickBy } from 'lodash';
import { ListFilters } from 'app/components/organisms/crud/form-filters/form-filters-type';
import { TFunction } from 'react-i18next';
import { DateTime } from 'app/components/atoms/date-time/date-time';
import React from 'react';
import { normalizeTemplate } from 'app/components/organisms/crud/utils';
import {
  CrudAddType,
  CrudListType,
  CrudRemoveType,
  CrudTemplate,
  ListColumns
} from 'app/components/organisms/crud/form-types';
import { formFields } from './form-fields';
import { PharmacyIntegration } from 'app/types/pharmacy-consents';
import { IdCopy } from 'app/components/atoms/id-copy/id-copy';
import { ApiKey } from 'app/components/atoms/api-key/api-key';

/**
 * List labels.
 */

const listColumns = (translate: TFunction): ListColumns<PharmacyIntegration> => [
  {
    title: translate('common.table.columns.name'),
    dataIndex: 'id',
    key: 'id',
    size: '100px',
    render: item => {
      return <IdCopy>{item.id}</IdCopy>;
    }
  },
  {
    title: translate('common.table.columns.driver'),
    dataIndex: 'driver',
    key: 'driver',
    render: item => item.driverId
  },
  {
    title: translate('common.table.columns.name'),
    dataIndex: 'name',
    key: 'name',
    size: '1fr',
    render: value => value.name
  },
  {
    title: translate('common.table.columns.apiKey'),
    dataIndex: 'apiKey',
    key: 'apiKey',
    size: '1fr',
    render: value => {
      return <ApiKey>{value.apiKey}</ApiKey>;
    }
  },
  {
    title: translate('common.table.columns.status'),
    dataIndex: 'status',
    key: 'status',
    render: value => value.status
  },
  {
    title: translate('common.table.columns.externalId'),
    dataIndex: 'externalId',
    key: 'externalId',
    render: value => value.externalId
  },
  {
    title: translate('common.table.columns.createdAt'),
    dataIndex: 'createdAt',
    key: 'createdAt',
    render: value => <DateTime date={value.createdAt} />
  },
  {
    title: translate('common.table.columns.updatedAt'),
    dataIndex: 'updatedAt',
    key: 'updatedAt',
    render: value => <DateTime date={value.updatedAt} />
  }
];

/**
 * List filters.
 */

const listFilters = (translate: TFunction): ListFilters => [
  {
    type: 'inputSearch',
    inline: true,
    name: 'search',
    label: translate('common.labels.search')
  }
];

/**
 * List.
 */

function list(translate: TFunction): CrudListType<PharmacyIntegration> {
  return {
    columns: listColumns(translate),
    filters: listFilters(translate),
    route: appRoutes.dashboard.services.pharmacies.integrations,
    enableExport: true,
    addButton: translate('common.actions.add'),
    canEdit: false,
    canRemove: true,
    resolveParams: (params: any, item: any) => {
      return {
        pharmacyId: params?.id ?? item?.id,
        id: params?.phoneNumber ?? item?.phoneNumber
      };
    },
    key: ['pharmaciesIntegrations'],
    normalizeFilters: (data: any) => {
      const { ...filters } = data.filters;

      const result: any = {
        filters: {
          ...pickBy(filters, identity)
        },
        order: []
      };

      return result;
    },
    normalizeData: (result: any) => {
      return result;
    },
    pagination: {
      pageSize: 25,
      pageSizeOptions: [10, 25, 50, 100, 500, 1000]
    },
    endpoint: apiEndpoints.pharmaciesIntegrations,
    endpointById: apiEndpoints.pharmaciesIntegrationsById
  };
}

/**
 * Remove.
 */

const remove = (): CrudRemoveType<PharmacyIntegration> => ({
  endpoint: apiEndpoints.pharmaciesIntegrationsById,
  resolveParams: (params: any, item?: PharmacyIntegration) => {
    return {
      pharmacyId: params?.pharmacyId,
      id: params?.id ?? item?.id
    };
  }
});

/**
 * Add.
 */

const add = (translate: TFunction): CrudAddType<PharmacyIntegration> => ({
  formFields: formFields(translate),
  normalizePayload(values) {
    return {
      ...values,
      metadata: '{}'
    };
  }
});

/**
 * Config.
 */

export function createPharmaciesIntegrationsTemplate(translate: TFunction): CrudTemplate<PharmacyIntegration> {
  return normalizeTemplate(translate, {
    list: list(translate),
    remove: remove(),
    add: add(translate)
  });
}
